import { APP_CHAIN } from '@/client/confs/app';
import { useTransactionActions, useTransactionToastHidden } from '@/client/hooks/use-transaction-actions';
import { useTransactionRecovery } from '@/client/hooks/use-transaction-recovery';
import { canResumeTransaction, isActiveTransaction, useTransactionStore } from '@/client/store/transaction-store';
import { useTranslation } from 'react-i18next';

export const TransactionStatus = () => {
  useTransactionRecovery();
  const { t } = useTranslation();
  // Record selection, action conditions and handlers are shared with the trade
  // panel (components/trade/panel-transaction-actions), so the two never diverge.
  const { record, canRecheck, canAcknowledgeCancel, recheck, acknowledgeCancel } = useTransactionActions();
  const hidden = useTransactionToastHidden(record);
  if (!record) return null;
  // Mobile: a full-height trade drawer starts 52px from the top, so this toast
  // would cover its title, tabs and options. While a drawer for this record's
  // market is open it shows the toast's actions itself; the toast comes back
  // when it closes. Records of other markets keep the toast. Recovery above
  // keeps running.
  if (hidden) return null;
  return (
    <aside
      role="status"
      // Drawers hosting the trade panel ignore outside interactions inside this
      // toast (see utils/transaction-status-target).
      data-transaction-status=""
      // Mobile: full width at the top, below the h-13 (3.25rem) market header and
      // the notch, so it never covers the trade drawer's amount input or the
      // bottom nav. pointer-events-auto keeps it tappable while a modal drawer
      // has set pointer-events: none on <body>. md+: bottom-left card.
      className="fixed left-4 right-4 top-[calc(3.25rem_+_env(safe-area-inset-top)_+_0.5rem)] z-[100] pointer-events-auto rounded-xl border bg-background p-4 text-sm shadow-lg md:top-auto md:right-auto md:bottom-4 md:max-w-sm"
    >
      <p>
        {t(
          record.replacementChanged
            ? 'transaction-replaced'
            : !isActiveTransaction(record)
              ? record.failureReason === 'insufficient-gas'
                ? 'transaction-insufficient-gas'
                : record.failureReason === 'wrong-chain'
                  ? 'transaction-wrong-chain'
                  : record.failureReason === 'reverted'
                    ? 'transaction-not-sent'
                    : `transaction-${record.stage}`
              : canResumeTransaction(record)
                ? 'transaction-reconfirm'
                : !record.hash
                  ? 'transaction-wallet-pending'
                  : record.stage === 'unknown'
                    ? 'transaction-unknown'
                    : 'transaction-pending'
        )}
      </p>
      {record.hash ? (
        <>
          <a
            className="block underline"
            href={`${APP_CHAIN.blockExplorers?.default.url ?? 'https://bscscan.com'}/tx/${record.hash}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {record.hash.slice(0, 10)}…{record.hash.slice(-6)}
          </a>
          <button disabled={!canRecheck} className="mt-2 underline disabled:hidden" onClick={recheck}>
            {t('transaction-recheck')}
          </button>
        </>
      ) : (
        canAcknowledgeCancel && (
          <button className="mt-2 underline" onClick={acknowledgeCancel}>
            {t('transaction-wallet-cancelled')}
          </button>
        )
      )}
      {!isActiveTransaction(record) && (
        <button
          className="mt-2 underline"
          onClick={() => useTransactionStore.getState().dismissThrough(record.chainId, record.account, record.startedAt)}
        >
          {t('transaction-dismiss')}
        </button>
      )}
    </aside>
  );
};
